// frontend/admin-portal/src/pages/Users.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  PageHeader,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Input,
  Can,
} from "@fiberhome/shared-ui";
import { userService } from "../services/userService";
import { roleService } from "../services/roleService";
import UserStats from "../components/users/UserStats";
import UserModal from "../components/users/UserModal";
import ResetPasswordModal from "../components/users/ResetPasswordModal";

export default function Users() {
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: "",
    roleId: "",
    isActive: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadRoles = useCallback(async () => {
    try {
      setRoles(await roleService.list());
    } catch {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      for (const [k, v] of Object.entries(filters)) {
        if (v !== "" && v != null) params[k] = v;
      }
      const { items, pagination } = await userService.list(params);
      setItems(items);
      setPagination(pagination);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);
  useEffect(() => {
    load();
  }, [load]);

  const handleNew = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const handleEdit = (u) => {
    setEditing(u);
    setModalOpen(true);
  };
  const handleSaved = () => {
    setRefreshKey((k) => k + 1);
    load();
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.fullName}"?`)) return;
    try {
      await userService.remove(u.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const handleToggle = async (u) => {
    try {
      await userService.toggleActive(u.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const handleUnlock = async (u) => {
    try {
      await userService.unlock(u.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const handleForceLogout = async (u) => {
    if (!window.confirm(`Force logout ${u.fullName} from all sessions?`))
      return;
    try {
      await userService.forceLogout(u.id);
      alert("Sessions revoked");
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const columns = [
    {
      key: "fullName",
      label: "User",
      render: (r) => (
        <div className="d-flex align-items-center gap-2">
          <div className="fh-user-avatar">
            {(r.fullName || r.username || "?")
              .split(" ")
              .map((x) => x[0])
              .slice(0, 2)
              .join("")
              .toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>{r.fullName}</div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              @{r.username} · {r.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      label: "Role",
      render: (r) =>
        r.role ? (
          <Badge variant={r.role.name === "admin" ? "danger" : "primary"}>
            {r.role.displayName}
          </Badge>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: "isActive",
      label: "Status",
      render: (r) => (
        <div className="d-flex gap-1">
          <Badge variant={r.isActive ? "success" : "default"}>
            {r.isActive ? "Active" : "Disabled"}
          </Badge>
          {r.isLocked && <Badge variant="danger">Locked</Badge>}
        </div>
      ),
    },
    {
      key: "lastLoginAt",
      label: "Last Login",
      render: (r) =>
        r.lastLoginAt ? (
          new Date(r.lastLoginAt).toLocaleDateString()
        ) : (
          <span className="text-muted">Never</span>
        ),
    },
    {
      key: "actions",
      label: "",
      width: 320,
      render: (r) => (
        <div className="d-flex gap-1 flex-wrap">
          <Can permission="users.write">
            <Button size="sm" variant="outline" onClick={() => handleEdit(r)}>
              Edit
            </Button>
          </Can>
          <Can permission="users.write">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setResetUser(r);
                setResetOpen(true);
              }}
            >
              Reset PW
            </Button>
          </Can>
          {r.isLocked && (
            <Can permission="users.write">
              <Button size="sm" variant="ghost" onClick={() => handleUnlock(r)}>
                Unlock
              </Button>
            </Can>
          )}
          <Can permission="users.write">
            <Button size="sm" variant="ghost" onClick={() => handleToggle(r)}>
              {r.isActive ? "Disable" : "Enable"}
            </Button>
          </Can>
          <Can permission="users.delete">
            <Button size="sm" variant="ghost" onClick={() => handleDelete(r)}>
              Delete
            </Button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Manage staff accounts and role assignments"
        breadcrumbs={["Home", "Users"]}
        actions={
          <Can permission="users.write">
            <Button variant="primary" onClick={handleNew}>
              + New User
            </Button>
          </Can>
        }
      />

      <UserStats refreshKey={refreshKey} />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <div className="fh-supplier-filters">
          <Input
            placeholder="Search name, email, username..."
            value={filters.search}
            onChange={(e) =>
              setFilters({ ...filters, search: e.target.value, page: 1 })
            }
          />
          <select
            className="fh-input"
            value={filters.roleId}
            onChange={(e) =>
              setFilters({ ...filters, roleId: e.target.value, page: 1 })
            }
          >
            <option value="">All roles</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.displayName}
              </option>
            ))}
          </select>
          <select
            className="fh-input"
            value={filters.isActive}
            onChange={(e) =>
              setFilters({ ...filters, isActive: e.target.value, page: 1 })
            }
          >
            <option value="">All statuses</option>
            <option value="true">Active only</option>
            <option value="false">Disabled only</option>
          </select>
        </div>
      </Card>

      <Card
        title={`Users (${pagination.total})`}
        actions={
          <div className="fh-products-toolbar__pager">
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            >
              Prev
            </Button>
            <span className="text-muted" style={{ fontSize: 12 }}>
              Page {pagination.page} / {pagination.pages || 1}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            >
              Next
            </Button>
          </div>
        }
      >
        <Table
          columns={columns}
          data={items}
          loading={loading}
          emptyMessage="No users"
        />
      </Card>

      <UserModal
        open={modalOpen}
        user={editing}
        roles={roles}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />
      {resetUser && (
        <ResetPasswordModal
          open={resetOpen}
          user={resetUser}
          onClose={() => {
            setResetOpen(false);
            setResetUser(null);
          }}
        />
      )}
    </>
  );
}
